import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Crown, Calendar, Mic, Bell, Mail, Lock, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [wednesdayNotifications, setWednesdayNotifications] = useState(user?.wednesday_notifications || false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/disclaimer");
  };

  const toggleWednesdayNotifications = async () => {
    const newValue = !wednesdayNotifications;
    setWednesdayNotifications(newValue);
    
    try {
      await axios.post(
        `${API}/auth/notifications`,
        { wednesday_notifications: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newValue ? "Wednesday notifications enabled!" : "Notifications disabled");
    } catch (error) {
      setWednesdayNotifications(!newValue); // Revert
      toast.error("Failed to update notifications");
    }
  };

  const requestEmailVerification = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/request-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVerificationSent(true);
      toast.success("Verification email sent!");
    } catch (error) {
      toast.error("Failed to send verification email");
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async () => {
    if (!resetEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email: resetEmail });
      toast.success("If the email exists, a reset link has been sent");
      setShowPasswordReset(false);
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const tierLabels = {
    free: "Free",
    basic: "Basic",
    premium: "Premium",
  };

  const tierColors = {
    free: "text-muted-foreground",
    basic: "text-primary",
    premium: "text-amber-500",
  };

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/persona")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        </div>

        {/* Profile header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl font-bold text-primary">
              {user?.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <h1 className="font-serif text-2xl font-bold mb-1">
            {user?.email}
          </h1>
          <div className={`flex items-center justify-center gap-2 ${tierColors[user?.subscription_tier]}`}>
            {user?.subscription_tier !== "free" && <Crown className="w-4 h-4" />}
            <span className="font-semibold">{tierLabels[user?.subscription_tier]}</span>
          </div>
          
          {/* Email verification status */}
          <div className="mt-2">
            {user?.email_verified ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-500">
                <CheckCircle className="w-3 h-3" />
                Email verified
              </span>
            ) : (
              <button
                onClick={requestEmailVerification}
                disabled={loading || verificationSent}
                className="text-xs text-primary hover:underline disabled:opacity-50"
                data-testid="verify-email-btn"
              >
                {verificationSent ? "Check your email" : "Verify email"}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-card rounded-2xl p-4 border border-border/50">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Mic className="w-4 h-4" />
              <span className="text-sm">Free Vents</span>
            </div>
            <span className="font-serif text-2xl font-bold">
              {user?.subscription_tier === "free" 
                ? user?.free_vents_remaining 
                : "∞"}
            </span>
          </div>
          
          <div className="bg-card rounded-2xl p-4 border border-border/50">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Expires</span>
            </div>
            <span className="font-serif text-lg font-bold">
              {user?.subscription_expires 
                ? new Date(user.subscription_expires).toLocaleDateString() 
                : "—"}
            </span>
          </div>
        </div>

        {/* Wednesday Notifications */}
        <div className="bg-card/50 rounded-3xl p-6 border border-border/50 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="font-semibold">Wednesday Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  Get reminded on Vent-to-Me Wednesdays
                </p>
              </div>
            </div>
            <Switch
              checked={wednesdayNotifications}
              onCheckedChange={toggleWednesdayNotifications}
              data-testid="wednesday-notifications-switch"
            />
          </div>
        </div>

        {/* Subscription info */}
        <div className="bg-card/50 rounded-3xl p-6 border border-border/50 mb-6">
          <h3 className="font-semibold mb-4">Your Plan</h3>
          
          <div className="space-y-3">
            {user?.subscription_tier === "free" ? (
              <>
                <p className="text-muted-foreground text-sm">
                  You're on the free plan with {user?.free_vents_remaining} vents remaining.
                </p>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
                  data-testid="upgrade-btn"
                >
                  Upgrade Now
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">
                  You have unlimited vents with {user?.subscription_tier === "premium" ? "interactive sessions" : "extended time"}.
                </p>
                <Button
                  onClick={() => navigate("/pricing")}
                  variant="outline"
                  className="w-full h-12 rounded-full border-border/50"
                  data-testid="manage-subscription-btn"
                >
                  {user?.subscription_tier === "basic" ? "Upgrade to Premium" : "Manage Subscription"}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Password Reset Section */}
        <div className="bg-card/50 rounded-3xl p-6 border border-border/50 mb-6">
          <button
            onClick={() => setShowPasswordReset(!showPasswordReset)}
            className="flex items-center gap-3 w-full"
            data-testid="password-reset-toggle"
          >
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Lock className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold">Password Reset</h3>
              <p className="text-sm text-muted-foreground">Change your password</p>
            </div>
          </button>
          
          {showPasswordReset && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-4 space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-muted-foreground">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="pl-12 h-12 rounded-xl bg-input/50"
                    data-testid="reset-email-input"
                  />
                </div>
              </div>
              <Button
                onClick={requestPasswordReset}
                disabled={loading}
                className="w-full h-12 rounded-full"
                data-testid="send-reset-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </motion.div>
          )}
        </div>

        {/* Logout */}
        <div className="mt-auto pb-4">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
